import { RequestContext } from 'src/lib/js/context'
import { getHost, getHostnameFromURL, getPath } from 'src/lib/js/utilities'

import config from 'config'

export const DEFAULT_HOME_PATH = '/'

export const TOAST_LOCAL_PATH = '/local'
export const TOAST_LOCAL_CATERING_PATH = '/local/catering'
export const TOAST_LOCAL_VENUES_PATH = '/local/venues'
export const TOAST_LOCAL_ACCOUNT_PATH = '/local/account'
export const TOAST_LOCAL_ORDER_PATH = '/local/order'
export const TOAST_LOCAL_SEARCH_PATH = '/local/search'
export const TOAST_LOCAL_CATERING_SEARCH_PATH = '/local/catering/search'
export const TOAST_LOCAL_VENUES_SEARCH_PATH = '/local/venues/search'
export const TOASTTAB_BOO_PATH = '/online'
export const TOASTTAB_BOO_LOCATIONS_PATH = '/online/locations'
export const TOAST_APP_ORDER_PATH = '/r'

const TOAST_LOCAL_NO_SHORT_URL_SUB_PATHS: ReadonlySet<string> = new Set([
  'account',
  'catering',
  'search',
  'venues'
])

/**
 * OO is all online ordering surfaces, ecomm is the ecommerce flow.
 *
 * When the cart channel is set to ECOMM, we skip all fulfillment validation and ordering enabled validation,
 * so ecomm carts can be created and orders can be placed at any time. Ecommerce pages also show no
 * fulfillment options as fulfillment is entirely dictated by selected shipping options and
 * via the integration with shipping providers
 */
export enum Channel {
  OO = 'oo',
  ECOMM = 'ecomm'
}

export const isHost = (host: string, staticContext?: RequestContext) => {
  // Node types this as string | string[] | undefined — it repeats into an array
  // when more than one proxy sets it. Take the first entry in that case.
  const rawForwardedHost = staticContext?.req?.headers['x-forwarded-host']
  const xForwardedHost = Array.isArray(rawForwardedHost)
    ? rawForwardedHost[0]
    : rawForwardedHost
  // x-forwarded-host may include a port (e.g. Next.js stamps host:port via base-server.js)
  // and can technically be a comma-separated list when multiple proxies are in the chain.
  // Strip both before comparing against config host values, which never include a port.
  const effectiveHost =
    (xForwardedHost || getHost(staticContext) || '')
      .split(',')[0]
      ?.trim()
      .split(':')[0] ?? ''
  return effectiveHost === host
}

// Request proxied from Toast Local (i.e. BOO workflow on a TL Rx Profile Page)
export const isToastLocalRequest = (staticContext?: RequestContext) => {
  const path = getPath(staticContext)
  return (
    isHost(config.resources.toastLocalHost, staticContext) &&
    path.startsWith(TOAST_LOCAL_PATH)
  )
}

// Request from the toasttab ordering domain
export const isToastOrderLocationsRequest = (
  staticContext?: RequestContext
) => {
  const path = getPath(staticContext)
  return (
    isHost(config.resources.toastOrderHost, staticContext) &&
    path.startsWith(TOASTTAB_BOO_LOCATIONS_PATH)
  )
}

// Request from the toasttab ordering domain
export const isToastOrderRequest = (staticContext?: RequestContext) => {
  const path = getPath(staticContext)
  return (
    isHost(config.resources.toastOrderHost, staticContext) &&
    path.startsWith(TOASTTAB_BOO_PATH)
  )
}

// Request from toast.app or book.toasttab.com with /r/:slug/order pattern
export const isToastAppOrderRequest = (staticContext?: RequestContext) => {
  const path = getPath(staticContext)
  const toastAppHost = config.resources.toastAppHost
  const bookHost = config.resources.bookHost
  const isValidHost =
    (toastAppHost && isHost(toastAppHost, staticContext)) ||
    (bookHost && isHost(bookHost, staticContext))
  return isValidHost && path.startsWith(`${TOAST_APP_ORDER_PATH}/`)
}

/**
 * Any surface that serves Toast Local restaurants: www.toasttab.com/local/* and
 * the toast.app / book.toasttab.com /r/:slug/* pages that /local traffic is
 * redirected to (see the redirectToastLocal middleware).
 *
 * Use this instead of isToastLocalRequest for behavior that depends on a
 * restaurant's Toast Local module rather than on the /local URL itself —
 * otherwise Toast-Local-only restaurants (onlineOrderingEnabled false,
 * toastLocalEnabled true) break once traffic moves to toast.app.
 */
export const isToastLocalSurfaceRequest = (staticContext?: RequestContext) =>
  isToastLocalRequest(staticContext) || !!isToastAppOrderRequest(staticContext)

const getReferrerHostname = (staticContext?: RequestContext) => {
  const referrer =
    typeof window !== 'undefined'
      ? window.__REFERRER__
      : staticContext?.req?.headers.referer
  return getHostnameFromURL(referrer as string)
}

// Paths that are loaded in iframes and should accept shortUrl from query params
const IFRAME_PATHS: ReadonlySet<string> = new Set(['/map', '/adyen', '/iframe'])

// Every prefixed form the iframe routers are mounted at (startup.ts). Server-side the
// path arrives post-strip, but client-side getPath() reads window.location.pathname,
// which still carries the prefix — normalising here keeps both sides in agreement.
// Matches any version rather than reading config.apiVersion, mirroring the dual
// router's own strip (server-https.ts) and keeping this module import-safe: it is
// evaluated at module scope, and several test suites mock 'config' without a default.
const IFRAME_PATH_PREFIX = /^\/(?:sites-web\/)?v\d+\//

const isIframePath = (staticContext?: RequestContext) =>
  IFRAME_PATHS.has(getPath(staticContext).replace(IFRAME_PATH_PREFIX, '/'))

// Grab the short URL for a restaurant. For Rx Profile pages, this will be the third
// item in the URL path. For MapPage and AdyenPage (loaded in iframes), a query string
// determines which Rx should be loaded.
export const getSiteShortUrl = (
  staticContext?: RequestContext,
  shortUrlQueryString?: string | null
) => {
  if (isToastOrderLocationsRequest(staticContext)) {
    // e.g. https://www.toasttab.com/online/locations/534c0f5e-2052-48f9-9608-76b30ac51186
    return ''
  } else if (isToastAppOrderRequest(staticContext)) {
    // e.g. https://toast.app/r/max-fried-chicken/order
    const pathParts = getPath(staticContext).split('/')
    // For /r/:slug/order pattern, slug is at index 2
    return pathParts.length >= 3 ? decodeURIComponent(pathParts[2] || '') : ''
  } else if (isToastOrderRequest(staticContext)) {
    // e.g. https://order.toasttab.com/online/max-fried-chicken
    return decodeURIComponent(getPath(staticContext).split('/')[2] || '')
  } else if (isToastLocalRequest(staticContext)) {
    const pathParts = getPath(staticContext).split('/')
    if (
      pathParts.length >= 3 &&
      TOAST_LOCAL_NO_SHORT_URL_SUB_PATHS.has(pathParts[2] || '')
    ) {
      return ''
    }

    if (pathParts.length >= 4 && pathParts[2] === 'order') {
      // e.g. https://www.toasttab.com/local/order/max-fried-chicken
      return decodeURIComponent(pathParts[3] || '')
    } else if (pathParts.length >= 3) {
      // e.g. https://www.toasttab.com/local/max-fried-chicken
      return decodeURIComponent(pathParts[2] || '')
    } else {
      return ''
    }
  } else {
    // For iframe routes (like /adyen, /map), accept the shortUrl query param
    // regardless of referrer hostname. This allows customers on custom domains
    // to properly load iframe content.
    if (shortUrlQueryString && isIframePath(staticContext)) {
      return shortUrlQueryString
    }
    const referrerHostname = getReferrerHostname(staticContext)
    return (referrerHostname === config.resources.toastLocalHost ||
      referrerHostname === config.resources.toastOrderHost) &&
      shortUrlQueryString
      ? shortUrlQueryString
      : ''
  }
}

export const getSiteMgmtGroupGuid = (staticContext?: RequestContext) => {
  if (isToastOrderLocationsRequest(staticContext)) {
    // e.g. https://www.toasttab.com/online/locations/534c0f5e-2052-48f9-9608-76b30ac51186
    return getPath(staticContext).split('/')[3] || ''
  }

  return ''
}

export const getSiteRef = (staticContext?: RequestContext) => {
  if (isToastOrderLocationsRequest(staticContext)) {
    // e.g. https://www.toasttab.com/online/locations/534c0f5e-2052-48f9-9608-76b30ac51186/default
    return decodeURIComponent(getPath(staticContext).split('/')[4] || '')
  }

  return ''
}

export const getSiteDomain = (staticContext?: RequestContext) => {
  const referrerHostname = getReferrerHostname(staticContext)
  const host = getHost(staticContext)
  // If the host isn't the main sites domain, it's a custom site so we can just use that.
  // Otherwise, we're probably trying to load in an iframe so we need to validate the referer
  const siteDomain = host !== config.server.host ? host : referrerHostname || ''

  if (!siteDomain) {
    return undefined
  }

  return siteDomain
}

const orderRoutesRegex = /^\/(order|checkout|confirm)(\/|$).*/
/**
 * @param path
 * @returns {boolean} true if the path is not part of the orderRoutes path topology for Websites.
 */
export const isWebsiteNonOrderRoute = (path: string) => {
  if (!path) return false
  return !orderRoutesRegex.test(path)
}

export const isToastLocalOrderRequest = (staticContext?: RequestContext) => {
  const path = getPath(staticContext)
  return (
    isToastLocalRequest(staticContext) &&
    path.startsWith(TOAST_LOCAL_ORDER_PATH)
  )
}
